import { useContext, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BoxFull, Button, Dropdown, InputText, TransactionalSubheader } from "iuvity-ui-components";
import { Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { AppContext } from "../../context/index";
import { ErrorMessage } from "@hookform/error-message";
import { ApplicationCreatorSchema } from "../../validations/schemas";
import { useTenants } from "../../hooks/useTenants";
import { useEnvironments } from "../../hooks/useEnvironments";
import { useVersions } from "../../hooks/useVersions";
import { useApplications } from "../../hooks/useApplications";
import { InformationApplication } from "../../components/applications/InformationApplication";
import { ImasterCategoryResponse } from "../../interfaces/roles.interface";
import { useMasterCategory } from "../../hooks/useMasterCategory";
import { REVALIDATE_FORM_PROPS } from "../../constants";

interface NewApplicationProps {
  update?: boolean;
}

export const applicationCreatorDefaultValues = {
  name: "",
  segment: "",
  currentversion: "",
};

export const NewApplicationPage: React.FC<NewApplicationProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, tenantId, selectedTenantId, selectedApplication },
  } = useContext(AppContext);

  const { applicationAction } = useApplications("ACTIONS");
  const { data: masterCategoriesList }: { data: ImasterCategoryResponse[] } = useMasterCategory("DATA");
  const segmentTypes: ImasterCategoryResponse[] = masterCategoriesList?.filter((category: ImasterCategoryResponse) => (
    category.status === 'ACTIVE' &&
    category.categoryId === 'SEGMENT_OPTIONS'));
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [visibleModelInformationApp, setVisibleModelInformationApp] = useState<boolean>(false);

  const {
    data: applicationsData,
  } = useApplications("DATA");
  const { data: tenantsData } = useTenants("DATA");
  const { data: envsData } = useEnvironments("DATA");
  const { data: versionsData } = useVersions("DATA");

  const {
    control,
    getValues,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues:
    {
      ...applicationCreatorDefaultValues,
      createdby: user?.name,
      tenantid: selectedTenantId || tenantId,
      segment: selectedApplication ? selectedApplication?.segment : null,
      name: selectedApplication ? selectedApplication?.name : null,
      currentversion: selectedApplication ? selectedApplication?.currentversion : null
    }
  });

  const formOptions: any = useMemo(() => {
    if (applicationsData && tenantsData && envsData && versionsData) {
      return {
        applications: applicationsData
          .reduce(function (result: any, element: any) {
            if (
              ![
                ...new Set(result.map((tenantData: any) => tenantData.value)),
              ].includes(element.appid)
            ) {
              result.push({
                label: element.name,
                value: element.id,
                tenantid: element.tenantid,
              });
            }
            return result;
          }, [])
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        segments: segmentTypes?.map(function (item: ImasterCategoryResponse) {
          return {
            label: item.id,
            value: item.id,
          };
        }, [])
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        tenants: tenantsData.filter((tenant: any) => (
          tenant.status !== 'DELETED' &&
          tenant.status !== 'DRAFT'
        ))
          .map((tenant: any) => ({
            label: tenant.name,
            value: tenant.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        environments: envsData
          .map((env: any) => ({
            label: env.name,
            value: env.id,
            applicationid: env.applicationid,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        versions: versionsData
          .map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
      };
    } else {
      return;
    }
  }, [applicationsData, tenantsData, envsData, versionsData, masterCategoriesList]);

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    let formValues = getValues();
    const actionStatus = await applicationAction("CREATE", [formValues], formValues?.name ?? undefined);
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Applications.createModalTitle`),
          content: t(`Applications.createModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Applications.cancelModalTitle"),
          content: t("Applications.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Applications.newApplicationTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={`TransactionalSubheader.BreadCrumb.${t("Applications.applicationsBreadcrumbLabel")}`}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Applications.applicationsBreadcrumbLabel"),
              disabled: true
            },
            {
              label: t("Applications.applicationAdminTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Applications.newApplicationTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="py-5"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }} className="wrapper-form-application">
          <p
            id="labelNewApplication"
            className="md"
            dangerouslySetInnerHTML={{ __html: t('Applications.newApplicationDescriptionLabel') }}
            style={{ lineHeight: 'var(--space-m)' }}
          />
          <div className="form-container md py-5 w-full ml-1">
            <div className="form md w-full">
              <div className="flex  flex-wrap">
                {Object.keys(applicationCreatorDefaultValues).filter(key => key != 'name').map((key) => {
                  return (
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={key}
                        name={key as any}
                        control={control}
                        rules={(ApplicationCreatorSchema(t) as any)[key]}
                        render={({ field }) => {
                          return ['name'].includes(key) ? (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <InputText
                                id={`input-label-${key}`}
                                label={t(`Applications.${key}Label`)}
                                placeholder={t(`Applications.${key}Placeholder`)}
                                className={errors.name?.message ? "p-invalid" : "mb-3 mt-2"}
                                type="text"
                                autoComplete="off"
                                keyfilter={ApplicationCreatorSchema(t)?.name?.keyfilter}
                                maxLength={ApplicationCreatorSchema(t)?.name?.maxLength?.value}
                                {...field}
                              />
                            </div>
                          ) : (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                id={`dropdown-${key}`}
                                className="dropdown mb-3"
                                label={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Label`)}
                                placeholder={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Placeholder`)}
                                options={formOptions?.[
                                  (key === "tenantid" ? "tenants" : "") ||
                                  (key === "segment" ? "segments" : "") ||
                                  (key === "currentversion" ? "versions" : "") ||
                                  (key === "installedversion" ? "versions" : "")
                                ]}
                                filter={false}
                                style={{
                                  zIndex: "6000 !important",
                                }}
                                emptyFilterMessage={t(`Applications.emptyLabel`)}
                                emptyMessage={t(`Applications.emptyLabel`)}
                                hasTooltipRef={["segment", "currentversion"].includes(key)}
                                tooltipRef={{
                                  content: t(`Applications.${key}Tooltip`),
                                  position: "bottom",
                                  id: `dropdown-type-${key}`,
                                }}
                                tooltipIdName={`dropdown-type-${key}`}
                                iconHasTooltip='iu iu-icon-info tooltip-dropdown-styles'
                                {...field}
                                showClear={field.value ? true : false}
                                onChange={(e: any) => {
                                  setValue(field.name, e.value || null, REVALIDATE_FORM_PROPS);
                                }}
                                //@ts-ignore
                                className={errors[key]?.message ? "p-invalid " : "dropdown"}
                              />
                            </div>
                          )
                        }}
                      />
                      {['currentversion', 'segment'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                          name={key}
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "4rem" }}
                            >
                              {t(message)}
                            </small>
                          )}
                        />}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <p id="objetiveApplicationLabel" className=" subtitle">
            {t('Applications.ObjectiveApplicationLabel')}
            <i className=" iu iu-icon-help act-btn" onClick={() => { setVisibleModelInformationApp(true) }} />
          </p>
          <div className="form-container md py-5 w-full ml-1">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">

                {Object.keys(applicationCreatorDefaultValues).filter(key => key == 'name').map((key) => {
                  return (
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={key}
                        name={key as any}
                        control={control}
                        rules={(ApplicationCreatorSchema(t) as any)[key]}
                        render={({ field }) => {
                          return ['name'].includes(key) ? (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <InputText
                                id={`input-label-${key}`}
                                label={t(`Applications.${key}Label`)}
                                placeholder={t(`Applications.${key}Placeholder`)}
                                className={errors.name?.message ? "p-invalid" : "mb-3 mt-2"}
                                type="text"
                                autoComplete="off"
                                keyfilter={ApplicationCreatorSchema(t)?.name?.keyfilter}
                                maxLength={ApplicationCreatorSchema(t)?.name?.maxLength?.value}
                                {...field}
                              />
                            </div>
                          ) : (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                id={`dropdown-${key}`}
                                className="dropdown mb-3"
                                label={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Label`)}
                                placeholder={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Placeholder`)}
                                options={formOptions?.[
                                  (key === "tenantid" ? "tenants" : "") ||
                                  (key === "segment" ? "segments" : "") ||
                                  (key === "currentversion" ? "versions" : "") ||
                                  (key === "installedversion" ? "versions" : "")
                                ]}
                                filter={false}
                                showClear={false}
                                style={{
                                  zIndex: "6000 !important",
                                }}
                                {...field}
                              />
                            </div>
                          )
                        }}
                      />
                      {['name'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                          name="name"
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "4rem" }}
                            >
                              {message}
                            </small>
                          )}
                        />}
                    </div>
                  )
                })}
                <div
                  className="flex flex-row mb-3 px-6"
                  style={{ width: "50%" }}
                >
                  <i className="help-name-application iu iu-icon-info act-btn mr-2" />
                  <p className='text-description-name'>
                    <div dangerouslySetInnerHTML={{ __html: t('Applications.DescriptionObjectiveApplicationText') }} />
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>

      </BoxFull>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("Agents.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          <Button
            variant="p-button"
            label={t("Agents.createBtnLabel")}
            disabled={!isValid}
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
      <InformationApplication visible={visibleModelInformationApp}
        closeHandler={() => { setVisibleModelInformationApp(false) }} />
    </>
  );
};